import type React from 'react';
import { useState } from 'react';

import { MaximizeIcon, MinimizeIcon } from '@vlabs/icons';
import validate from '@vlabs/shared/validators';
import {
  ButtonScrimGroup, Control, GeoLocation, GeoLocationMarker, Tooltip,
} from '@vlabs/uikit';
import { IconButton } from '@vlabs/uikit/components/controls/button/icon-button';
import { useDynamicCoordinates } from '@vlabs/uikit/components/geo-location/useDynamicCoordinates';
import cn from 'classnames';
import { createPortal } from 'react-dom';
import { useForm } from 'react-hook-form';

import { stripUndefined } from '@vlabs/monokit/shared/lib/utils';

import { useGeopositionTranslation } from './i18n';
import type { GroupStreamSettingsLocation } from '../stream-settings';
import st from './GeopositionForm.module.sass';

interface GeopositionFormValues {
  city?: string;
  area?: string;
  district?: string;
  street?: string;
  house_number?: string;
  geo_position: {
    latitude?: string | number;
    longitude?: string | number;
  };
}

interface GeopositionFormProps {
  defaultValues?: GroupStreamSettingsLocation;
  onSubmit: (location: GroupStreamSettingsLocation | undefined) => void;
  onCancel: VoidFunction;
}

const toNumber = (value: string | number | undefined): number | undefined => {
  if (value === '' || value === undefined || value === null) return undefined;
  const result = Number(value);
  return Number.isNaN(result) ? undefined : result;
};

const notEmpty = (value: string | undefined): string | undefined => (value === '' ? undefined : value);

function GeopositionForm({
  defaultValues = undefined,
  onSubmit: onSubmitLocation,
  onCancel,
}: GeopositionFormProps): React.JSX.Element {
  const { t } = useGeopositionTranslation();
  const [isFullScreen, setIsFullScreen] = useState(false);

  const {
    register,
    setValue,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm<GeopositionFormValues>({
    defaultValues: {
      city: defaultValues?.city ?? '',
      area: defaultValues?.area ?? '',
      district: defaultValues?.district ?? '',
      street: defaultValues?.street ?? '',
      house_number: defaultValues?.house_number ?? '',
      geo_position: {
        latitude: defaultValues?.geo_position?.latitude ?? '',
        longitude: defaultValues?.geo_position?.longitude ?? '',
      },
    },
  });

  const latitude = watch('geo_position.latitude');
  const longitude = watch('geo_position.longitude');
  const position = useDynamicCoordinates({
    latitude: latitude ?? '',
    longitude: longitude ?? '',
  });

  const onSubmit = handleSubmit((values) => {
    const nextLatitude = toNumber(values.geo_position.latitude);
    const nextLongitude = toNumber(values.geo_position.longitude);

    const nextLocation: GroupStreamSettingsLocation = {
      city: notEmpty(values.city),
      area: notEmpty(values.area),
      district: notEmpty(values.district),
      street: notEmpty(values.street),
      house_number: notEmpty(values.house_number),
      geo_position: stripUndefined({ latitude: nextLatitude, longitude: nextLongitude }),
    };

    onSubmitLocation(stripUndefined(nextLocation));
  });

  const onMapClick = ({ latLng }: { latLng: [number, number] }) => {
    setValue('geo_position.latitude', latLng[0]);
    setValue('geo_position.longitude', latLng[1]);
  };

  const fullScreenButton = (
    <Tooltip
      className={st.FullScreenButton}
      tooltipContent={isFullScreen ? t('exit_fullscreen') : t('fullscreen')}
      tooltipProps={{ place: 'bottom', positionStrategy: 'absolute' }}
    >
      <ButtonScrimGroup
        orientation="vertical"
        shape="round"
        size="medium"
      >
        <IconButton
          icon={isFullScreen ? <MinimizeIcon /> : <MaximizeIcon />}
          kind="secondary"
          onClick={() => setIsFullScreen((prev) => !prev)}
          variant="flat"
        />
      </ButtonScrimGroup>
    </Tooltip>
  );

  const map = (
    <div className={st.Map}>
      <GeoLocation
        center={position}
        mapProps={{
          onClick: onMapClick,
        }}
        toggleFullScreenMode={false}
      >
        <GeoLocationMarker anchor={position} />
      </GeoLocation>
      {fullScreenButton}
    </div>
  );

  const fields = (
    <div className={st.Fields}>
      <div className={st.Field}>
        <Control.Input
          errorMessage={errors.geo_position?.latitude?.message}
          errors={errors}
          id="geoposition-latitude"
          label={t('fields.latitude_input')}
          {...register('geo_position.latitude', {
            deps: ['geo_position.longitude'],
            min: validate.gte(-90, t('validation.number')),
            max: validate.lte(90, t('validation.number')),
            validate: {
              number: validate.number(t('validation.number')),
              requiredIfPair: (value) => {
                if ((value === '' || value === undefined) && longitude) return t('validation.required');
                return true;
              },
            },
          })}
        />
      </div>
      <div className={st.Field}>
        <Control.Input
          errorMessage={errors.geo_position?.longitude?.message}
          errors={errors}
          id="geoposition-longitude"
          label={t('fields.longitude_input')}
          {...register('geo_position.longitude', {
            deps: ['geo_position.latitude'],
            min: validate.gte(-180, t('validation.number')),
            max: validate.lte(180, t('validation.number')),
            validate: {
              number: validate.number(t('validation.number')),
              requiredIfPair: (value) => {
                if ((value === '' || value === undefined) && latitude) return t('validation.required');
                return true;
              },
            },
          })}
        />
      </div>
      <div className={st.Field}>
        <Control.Input
          errorMessage={errors.city?.message}
          errors={errors}
          id="geoposition-city"
          label={t('fields.city')}
          {...register('city', { maxLength: validate.maxLength(36, t('validation.max_length')) })}
        />
      </div>
      <div className={st.Field}>
        <Control.Input
          errorMessage={errors.area?.message}
          errors={errors}
          id="geoposition-area"
          label={t('fields.area')}
          {...register('area', { maxLength: validate.maxLength(36, t('validation.max_length')) })}
        />
      </div>
      <div className={st.Field}>
        <Control.Input
          errorMessage={errors.district?.message}
          errors={errors}
          id="geoposition-district"
          label={t('fields.district')}
          {...register('district', { maxLength: validate.maxLength(36, t('validation.max_length')) })}
        />
      </div>
      <div className={st.Field}>
        {/* Улица и дом — одно поле: лейблы в ряд, поля в ряд */}
        <div className={st.FieldRow}>
          <div className={st.StreetItem}>
            <Control.Input
              errorMessage={errors.street?.message}
              errors={errors}
              id="geoposition-street"
              label={t('fields.street')}
              {...register('street', { maxLength: validate.maxLength(36, t('validation.max_length')) })}
            />
          </div>
          <div className={st.HouseItem}>
            <Control.Input
              errorMessage={errors.house_number?.message}
              errors={errors}
              id="geoposition-house-number"
              label={t('fields.house_number')}
              {...register('house_number', { maxLength: validate.maxLength(36, t('validation.max_length')) })}
            />
          </div>
        </div>
      </div>

      <div className={st.Actions}>
        <Control.Button
          className={st.Action}
          type="submit"
        >
          {t('on_save')}
        </Control.Button>
        <Control.Button
          className={st.Action}
          kind="secondary"
          onClick={onCancel}
          type="button"
          variant="outlined"
        >
          {t('on_cancel')}
        </Control.Button>
      </div>
    </div>
  );

  if (isFullScreen) {
    return createPortal(
      <div className={st.FullScreen}>
        <div className={st.FullScreenMap}>
          <GeoLocation
            center={position}
            mapProps={{
              onClick: onMapClick,
              mouseEvents: false,
              touchEvents: false,
            }}
            toggleFullScreenMode={false}
          >
            <GeoLocationMarker anchor={position} />
          </GeoLocation>
          {fullScreenButton}
        </div>
        <div className={st.FullScreenPanel}>
          <form
            className={st.Form}
            onSubmit={onSubmit}
          >
            {fields}
          </form>
        </div>
      </div>,
      document.body,
    );
  }

  return (
    <div className={cn(st.ModalRoot)}>
      <form
        className={st.Form}
        onSubmit={onSubmit}
      >
        <h2 className={st.ModalTitle}>{t('title')}</h2>
        {map}
        {fields}
      </form>
    </div>
  );
}

export { GeopositionForm };
